import { useState } from "react";
import { Link } from "react-router-dom";
import { Mail, MapPin, Loader2, Check } from "lucide-react";
import { api } from "../lib/api";
import { useDocumentMeta } from "../hooks/useDocumentMeta";

const inputCls =
    "w-full border-[1.5px] border-border-strong rounded-[10px] bg-white text-navy text-[15px] px-3.5 outline-none transition-colors focus:border-navy";

export default function Contact() {
    useDocumentMeta({
        title: "Contact — FOD by DMB Partners",
        description: "Contactez FOD by DMB Partners pour auditer et optimiser vos frais bancaires PME et ETI. Réponse sous 24h ouvrées par nos fondateurs.",
        canonical: "https://www.fodbydmb.com/contact",
    });
    const [form, setForm] = useState({ name: "", email: "", company: "", phone: "", message: "", consent: false });
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [error, setError] = useState("");

    const update = (k, v) => setForm((p) => ({ ...p, [k]: v }));

    const submit = async (e) => {
        e.preventDefault();
        if (!form.consent) {
            setError("Merci de cocher la case d'acceptation.");
            return;
        }
        setError("");
        setSubmitting(true);
        try {
            const { consent, ...payload } = form;
            await api.post("/contact", payload);
            setSubmitted(true);
        } catch (err) {
            setError("Une erreur est survenue. Merci de réessayer.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div data-testid="contact-page">
            <div className="mx-auto max-w-[1180px] px-4 md:px-8 lg:px-12 pt-10 md:pt-16 lg:pt-20 pb-12 md:pb-16 lg:pb-24 flex flex-wrap gap-x-16 gap-y-10 items-start">
                <ContactIntro />
                <div className="flex-[999_1_520px] min-w-0">
                    {submitted ? (
                        <div data-testid="contact-success" className="bg-white border border-border-default rounded-2xl p-10 flex flex-col items-center text-center gap-3 animate-fade-up">
                            <span className="w-14 h-14 rounded-full bg-navy text-gold flex items-center justify-center"><Check size={28} strokeWidth={2.5} /></span>
                            <h2 className="m-0 text-2xl font-bold text-navy">Message envoyé.</h2>
                            <p className="m-0 text-grey-text-soft">Nous vous répondrons dans les meilleurs délais. Merci pour votre message.</p>
                        </div>
                    ) : (
                        <form
                            onSubmit={submit}
                            data-testid="contact-form"
                            className="bg-white border border-border-default rounded-2xl p-[22px] md:p-9 grid grid-cols-1 md:grid-cols-2 gap-[18px]"
                        >
                            <Field id="ct-nom" label="Nom" required autoComplete="name" value={form.name} onChange={(v) => update("name", v)} testid="contact-name" />
                            <Field id="ct-mail" label="E-mail" type="email" required autoComplete="email" value={form.email} onChange={(v) => update("email", v)} testid="contact-email" />
                            <Field id="ct-ent" label="Entreprise" autoComplete="organization" value={form.company} onChange={(v) => update("company", v)} testid="contact-company" />
                            <Field id="ct-tel" label="Téléphone" type="tel" autoComplete="tel" value={form.phone} onChange={(v) => update("phone", v)} testid="contact-phone" />
                            <div className="md:col-span-2 flex flex-col gap-1.5">
                                <Label htmlFor="ct-msg" required>Message</Label>
                                <textarea
                                    id="ct-msg"
                                    data-testid="contact-message"
                                    value={form.message}
                                    onChange={(e) => update("message", e.target.value)}
                                    required
                                    rows={6}
                                    className={`${inputCls} py-3 resize-y`}
                                />
                            </div>
                            <div className="md:col-span-2 flex gap-3 items-start">
                                <input
                                    id="ct-rgpd"
                                    type="checkbox"
                                    data-testid="contact-consent"
                                    checked={form.consent}
                                    required
                                    aria-required="true"
                                    onChange={(e) => update("consent", e.target.checked)}
                                    className="w-6 h-6 mt-0 accent-navy shrink-0"
                                />
                                <label htmlFor="ct-rgpd" className="text-[13px] leading-relaxed text-grey-text-soft">
                                    En cochant cette case, vous acceptez notre politique de confidentialité conforme au RGPD. <span className="text-gold-text">*</span>{" "}
                                    <Link to="/politique-de-confidentialite" className="text-navy underline underline-offset-2">Politique de confidentialité</Link>
                                </label>
                            </div>
                            {error && <p role="alert" data-testid="contact-error" className="md:col-span-2 m-0 text-sm text-error">{error}</p>}
                            <button
                                type="submit"
                                data-testid="contact-submit"
                                disabled={submitting}
                                className="md:col-span-2 h-[54px] rounded-[10px] bg-gold text-navy text-base font-semibold inline-flex items-center justify-center gap-2 transition-[transform,box-shadow,opacity] hover:-translate-y-[1px] hover:shadow-lg disabled:opacity-60"
                            >
                                {submitting && <Loader2 size={18} className="animate-spin" />}
                                Envoyer
                            </button>
                        </form>
                    )}
                </div>
            </div>
        </div>
    );
}

const ContactIntro = () => (
    <div className="flex-1 basis-[340px] min-w-0 flex flex-col gap-5 animate-fade-up">
        <div className="text-[13px] font-semibold uppercase tracking-[0.16em] text-gold-text">Contact</div>
        <h1 className="m-0 font-bold text-[32px] md:text-5xl lg:text-[54px] leading-[1.12] tracking-[-0.025em] text-navy">Un message à nous faire passer ?</h1>
        <div className="w-[140px] h-1.5 bg-gold" />
        <p className="m-0 text-base leading-[1.7] text-grey-text-soft">
            Nous vous répondrons dans les meilleurs délais. Pour gagner du temps, n'hésitez pas à utiliser notre{" "}
            <Link to="/simulateur" className="font-semibold text-navy underline underline-offset-2">simulateur</Link> en parallèle.
        </p>
        <div className="flex flex-col gap-3.5 mt-2">
            <InfoCard icon={Mail} label="E-mail">
                <a href="mailto:contact@fodbydmb.com" data-testid="contact-email-link" className="inline-flex items-center min-h-[44px] text-base font-semibold text-navy hover:text-gold-text transition-colors">contact@fodbydmb.com</a>
            </InfoCard>
            <InfoCard icon={MapPin} label="Adresse">
                <span className="text-base font-medium leading-normal text-navy">149 avenue du Maine, 75014 Paris, France</span>
            </InfoCard>
        </div>
    </div>
);

const InfoCard = ({ icon: Icon, label, children }) => (
    <div className="bg-white border border-border-default rounded-xl px-5 py-[18px] flex gap-3.5 items-center">
        <span className="w-11 h-11 rounded-[10px] bg-navy text-gold flex items-center justify-center shrink-0"><Icon size={20} /></span>
        <div className="flex flex-col min-w-0">
            <span className="text-xs font-semibold uppercase tracking-[0.12em] text-gold-text">{label}</span>
            {children}
        </div>
    </div>
);

const Label = ({ htmlFor, required, children }) => (
    <label htmlFor={htmlFor} className="text-sm font-semibold text-navy">
        {children} {required && <span className="text-gold-text">*</span>}
    </label>
);

function Field({ id, label, value, onChange, type = "text", required, testid, autoComplete }) {
    return (
        <div className="flex flex-col gap-1.5">
            <Label htmlFor={id} required={required}>{label}</Label>
            <input
                id={id}
                data-testid={testid}
                type={type}
                value={value}
                autoComplete={autoComplete}
                onChange={(e) => onChange(e.target.value)}
                required={required}
                className={`${inputCls} h-[50px]`}
            />
        </div>
    );
}
